import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, School as SchoolIcon, Loader2, Building, MapPin, Package, Coins } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getSupplyExecutiveDashboard, getSupplySchoolDetails } from '../api';

const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

/**
 * Supply-focused Schools page — ranks schools by ALLOCATED QUANTITY (not incidents).
 * Data sourced from a single aggregate call to /api/supply/rows via /schools-agg
 * — we build the aggregation client-side using rows already fetched by the
 * executive dashboard. For a simpler v6.4 we just re-use the dashboard's map
 * as the raw source and derive per-school totals from db.supply.
 */
export default function SchoolsListPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState(params.get('supplier') || '');
  const [selectedSchool, setSelectedSchool] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Use /api/supply/schools aggregate (see backend) — falls back to executive-dashboard for KPIs
      const base = process.env.REACT_APP_BACKEND_URL;
      const [aggRes, dashRes] = await Promise.all([
        fetch(`${base}/api/supply/schools`).then((r) => r.json()),
        getSupplyExecutiveDashboard(),
      ]);
      setRows(aggRes.schools || []);
      setKpis(dashRes.kpis);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suppliers = useMemo(() => Array.from(new Set(rows.map((r) => r.supplier).filter(Boolean))).sort(), [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (supplierFilter) list = list.filter((r) => r.supplier === supplierFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) =>
      (r.school_name || '').toLowerCase().includes(q) ||
      (r.school_id || '').toString().includes(q) ||
      (r.school_internal_id || '').toString().includes(q) ||
      (r.region || '').toLowerCase().includes(q) ||
      (r.governorate || '').toLowerCase().includes(q)
    );
    return list;
  }, [rows, supplierFilter, search]);

  const topRanked = useMemo(() => [...filtered].sort((a, b) => (b.allocated_qty || 0) - (a.allocated_qty || 0)).slice(0, 10), [filtered]);

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="schools-list">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>SCHOOLS · SUPPLY TRACKING</div>
            <h1 className="se-title mt-1">المدارس</h1>
            <div className="se-subtitle">جميع المدارس المشمولة في التوريد مع كمياتها المخصصة</div>
          </div>
        </div>

        {/* KPI row — supply metrics only */}
        <section className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="إجمالي المدارس" value={numFmt(kpis?.totalSchools)} unit="مدرسة" icon={SchoolIcon} testid="schools-kpi-total" />
            <Kpi label="الكمية المخصصة" value={numFmt(kpis?.allocatedQuantity)} unit="قطعة" icon={Package} tint="#9D8CFF" testid="schools-kpi-qty" />
            <Kpi label="الأصناف" value={numFmt(kpis?.totalItems)} unit="صنف" icon={Building} tint="#38D6C7" testid="schools-kpi-items" />
            <Kpi label="القيمة الإجمالية" value={moneyFmt(kpis?.allocatedValue)} unit="ريال" icon={Coins} tint="#38D6C7" testid="schools-kpi-value" />
          </div>
        </section>

        {/* Top ranked schools (was "المدارس الأعلى بلاغاً" — now REAL supply-based) */}
        {topRanked.length > 0 && (
          <section className="mb-6">
            <div className="se-section-head mb-3">المدارس الأعلى في الكميات المخصصة</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="top-schools">
              {topRanked.slice(0, 5).map((s, i) => (
                <div key={i} className="se-panel p-3 cursor-pointer hover:-translate-y-0.5 transition-transform"
                  onClick={() => setSelectedSchool(s.school_internal_id || s.school_id || s.school_name)}
                  data-testid={`top-school-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold"
                      style={{ background: 'rgba(109,93,251,0.2)', color: '#B7ADFF' }}>{i + 1}</span>
                    <div className="text-[12.5px] font-semibold truncate" style={{ color: '#F1EEFB' }} title={s.school_name}>{s.school_name || 'غير متوفر'}</div>
                  </div>
                  <div className="text-[10.5px] text-slate-400 mb-1">{s.governorate || '—'}</div>
                  <div className="text-[18px] font-extrabold tabular-nums" style={{ color: '#9D8CFF' }}>{numFmt(s.allocated_qty)}</div>
                  <div className="text-[10px] text-slate-500">قطعة · {numFmt(s.items_count)} صنف</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="se-input w-72 pr-8" placeholder="بحث بالاسم أو الرقم الوزاري"
              value={search} onChange={(e) => setSearch(e.target.value)} data-testid="schools-search" />
          </div>
          <select className="se-input" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} data-testid="schools-supplier-filter">
            <option value="">جميع الموردين</option>
            {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="text-slate-500 text-[11.5px] mr-auto">
            {numFmt(filtered.length)} مدرسة
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="se-panel p-12 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="se-panel p-12 text-center text-slate-400">لا توجد مدارس مطابقة</div>
        ) : (
          <div className="se-panel overflow-x-auto">
            <table className="se-table">
              <thead>
                <tr>
                  <th>اسم المدرسة</th>
                  <th>الرقم الوزاري</th>
                  <th>المورد</th>
                  <th>إدارة التعليم</th>
                  <th>المدينة</th>
                  <th>المرحلة</th>
                  <th className="text-center">الأصناف</th>
                  <th className="text-center">الكمية المخصصة</th>
                  <th className="text-center">القيمة</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 300).map((r, i) => (
                  <tr key={i} className="se-row-click" onClick={() => setSelectedSchool(r.school_internal_id || r.school_id || r.school_name)} data-testid={`schools-row-${i}`}>
                    <td className="font-semibold" style={{ color: '#F1EEFB' }}>{r.school_name || 'غير متوفر'}</td>
                    <td className="tabular-nums text-slate-400">{r.school_id || (r.school_id_missing ? <span title="بدون رقم وزاري — تم توليد معرف داخلي" style={{ color: '#F59E0B' }}>لا يوجد</span> : '—')}</td>
                    <td className="text-slate-400">{r.supplier || '—'}</td>
                    <td className="text-slate-400">{r.governorate || '—'}</td>
                    <td className="text-slate-400">{r.region || '—'}</td>
                    <td className="text-slate-400">{r.stage || '—'}</td>
                    <td className="text-center tabular-nums">{numFmt(r.items_count)}</td>
                    <td className="text-center tabular-nums font-bold" style={{ color: '#9D8CFF' }}>{numFmt(r.allocated_qty)}</td>
                    <td className="text-center tabular-nums">{moneyFmt(r.allocated_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 300 && (
              <div className="text-center text-slate-500 text-[11px] py-2">عرض 300 من {numFmt(filtered.length)} مدرسة</div>
            )}
          </div>
        )}

        {selectedSchool && <SchoolModal schoolId={selectedSchool} onClose={() => setSelectedSchool(null)} />}
      </main>
    </div>
  );
}

function Kpi({ label, value, unit, icon: Icon, tint = '#9D8CFF', testid }) {
  return (
    <div className="se-kpi" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="se-kpi-label">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}22`, border: `1px solid ${tint}44` }}>
          <Icon size={15} style={{ color: tint }} />
        </div>
      </div>
      <div className="se-kpi-value">{value}</div>
      {unit && <div className="se-kpi-unit">{unit}</div>}
    </div>
  );
}

function SchoolModal({ schoolId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getSupplySchoolDetails(schoolId).then((d) => { setData(d); setLoading(false); });
  }, [schoolId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,9,16,0.75)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div className="se-panel-hi max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ padding: 24 }}>
        {loading ? (
          <div className="p-10 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : !data?.found ? (
          <div className="p-10 text-center">
            <div className="font-bold" style={{ color: '#F1EEFB' }}>لا توجد بيانات توريد لهذه المدرسة</div>
            <button className="se-btn se-btn-primary mt-4" onClick={onClose}>إغلاق</button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold tracking-wider" style={{ color: '#B7ADFF' }}>SCHOOL SUPPLY PROFILE</div>
                <div className="text-[20px] font-bold mt-1" style={{ color: '#F1EEFB' }}>{data.school_name || 'غير متوفر'}</div>
                <div className="text-slate-400 text-[12px] mt-1">
                  الرقم الوزاري: {data.school_id || (data.school_id_missing ? <span style={{ color: '#F59E0B' }}>لا يوجد (معرف داخلي)</span> : 'غير متوفر')} · {data.region || 'غير متوفر'} {data.stage && `· ${data.stage}`}
                </div>
              </div>
              <button onClick={onClose} className="se-btn se-btn-ghost">إغلاق</button>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="text-[10px] text-slate-400">الأصناف</div>
                <div className="text-[16px] font-bold tabular-nums mt-0.5">{numFmt(data.summary.items_count)}</div>
              </div>
              <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="text-[10px] text-slate-400">الكمية</div>
                <div className="text-[16px] font-bold tabular-nums mt-0.5" style={{ color: '#9D8CFF' }}>{numFmt(data.summary.total_quantity)}</div>
              </div>
              <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="text-[10px] text-slate-400">القيمة</div>
                <div className="text-[16px] font-bold tabular-nums mt-0.5" style={{ color: '#38D6C7' }}>{moneyFmt(data.summary.total_value)}</div>
              </div>
              <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="text-[10px] text-slate-400">التوريد الفعلي</div>
                <div className="text-[12px] font-bold mt-0.5 text-slate-500">غير متوفر</div>
              </div>
            </div>
            <div className="se-panel overflow-x-auto" style={{ padding: 0 }}>
              <table className="se-table">
                <thead><tr><th>الصنف</th><th>المورد</th><th className="text-center">الكمية</th><th className="text-center">سعر الوحدة</th><th className="text-center">القيمة</th></tr></thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i}>
                      <td>{it.item || '—'}</td>
                      <td className="text-slate-400">{it.company || '—'}</td>
                      <td className="text-center tabular-nums">{numFmt(it.quantity)}</td>
                      <td className="text-center tabular-nums">{numFmt(it.unit_price)}</td>
                      <td className="text-center tabular-nums">{moneyFmt(it.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
